// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import {
  ReturnOrder,
  reasonToAPI,
  returnOrderSchema,
} from '@/return-order/api/types';
import {
  PHASE_CONFIG,
  ROPhase,
  ROSubstatus,
  ReturnOrderFilter,
  roPhaseSchema,
  roSubstatusSchema,
} from '@/return-order/constants/status';
import type { ReturnOrderSortProperty } from '@/return-order/hooks/use-return-orders';
import queryString from 'query-string';
import { z } from 'zod';

const SORT_PROPERTY_TO_BACKEND: Record<ReturnOrderSortProperty, string> = {
  roNumber: 'returnOrderNumber',
  status: 'status',
};

/**
 * Type definition for the paginated response.
 */
interface PagedReturnOrdersResponse {
  results: unknown[];
  totalCount: number;
}

/**
 * Type definition for the API response wrapper.
 */
interface ApiResponse {
  data: PagedReturnOrdersResponse;
  metadata: { requestId: string };
}

/**
 * Type definition for the get return orders API response.
 */
interface ReturnOrdersResponse {
  returnOrders: ReturnOrder[];
  resultCount: number;
}

export function getFilterStatuses(
  status?: ROPhase,
  subStatuses?: ROSubstatus[]
): string[] | undefined {
  if (status === undefined && subStatuses === undefined) {
    return undefined;
  }

  const phases: readonly ROPhase[] =
    status !== undefined ? [status] : roPhaseSchema.options;

  const allFilters = phases.flatMap((p) => PHASE_CONFIG[p].backendFilters);

  if (subStatuses === undefined || subStatuses.length === 0) {
    return allFilters;
  }

  const suffixes = new Set(subStatuses.map((m) => m.toUpperCase()));
  const allModifiers = new Set(
    roSubstatusSchema.options.map((m) => m.toUpperCase())
  );
  return allFilters.filter((s) => {
    const suffix = s.split('_').pop();
    if (suffix === undefined || !allModifiers.has(suffix)) {
      return false;
    }
    return suffixes.has(suffix);
  });
}

/**
 * Fetches return orders with the specified filter criteria.
 */
export async function getReturnOrders(
  filter?: ReturnOrderFilter
): Promise<ReturnOrdersResponse> {
  const url = '/v1/return-order';
  const response = await api.get<ApiResponse>(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      statuses: getFilterStatuses(filter?.status, filter?.subStatuses),
      types: filter?.reason ? [reasonToAPI(filter.reason)] : undefined,
      search: filter?.searchTerm,
      page: filter?.page,
      limit: filter?.pageSize,
      fromLocationIds: filter?.fromLocationIds,
      fromCustomerIds: filter?.fromCustomerIds,
      fromLocationCompanyTypes: filter?.fromLocationCompanyTypes,
      toLocationIds: filter?.toLocationIds,
      toCustomerIds: filter?.toCustomerIds,
      toLocationCompanyTypes: filter?.toLocationCompanyTypes,
      sortBy: filter?.sort
        ? SORT_PROPERTY_TO_BACKEND[filter.sort.property]
        : undefined,
      sortOrder: filter?.sort?.direction,
    },
  });

  // Parse and validate each return order with Zod
  const returnOrdersSchema = z.array(returnOrderSchema);
  const returnOrders = await parseResponse(
    returnOrdersSchema,
    response.data.data.results,
    {
      schemaName: 'ReturnOrders',
      endpoint: url,
    }
  );

  return {
    returnOrders,
    resultCount: response.data.data.totalCount,
  };
}
